import { useMutation, useQuery } from '@apollo/client';
import { Badge, Box, Drawer, LoadingOverlay, Text } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MUTATION_DELETE_TASK } from '../../../api/mutations';
import { QUERY_GET_TASKS, QUERY_GET_TASK_DETAIL } from '../../../api/queries';
import { FORM_MODE } from '../../../components/forms/FormBase';
import Page from '../../../components/page/Page';
import TaskForm from './TaskForm';
import { Paths } from '../../../routes/paths';

const TaskDetailPage = () => {
  let params = useParams();
  const navigate = useNavigate();

  const [opened, setOpened] = useState(false);

  const { id: taskId } = params || {};

  const { loading, error, data, refetch } = useQuery(QUERY_GET_TASK_DETAIL, {
    variables: {
      taskId: params.id,
    },
  });
  const [deleteMutation, { loading: deleting }] = useMutation(
    MUTATION_DELETE_TASK,
    {
      refetchQueries: [{ query: QUERY_GET_TASKS }],
    }
  );

  const handleConfirm = () => {
    deleteMutation({
      variables: {
        taskId,
      },
    })
      .then(({ data }) => {
        navigate(Paths.TaskList, { replace: true });
        showNotification({
          title: `Deleted task: ${data.deleteCampaign.title}`,
          message: '🤥',
        });
      })
      .catch((e) => {
        console.log('Error');
      });
  };

  const onDelete = () => {
    openConfirmModal({
      title: 'Delete task',
      centered: true,
      children: (
        <Text size="sm">Are you sure you want to delete this task?</Text>
      ),
      labels: { confirm: 'Delete task', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onCancel: () => console.log('Cancel'),
      onConfirm: () => handleConfirm(),
    });
  };

  const onSuccess = () => {
    refetch();
    setOpened(false);
    showNotification({
      title: 'Updated',
      message: 'Hey there, your code is awesome! 🤥',
    });
  };

  if (error) return <div>`Error! ${error.message}`</div>;

  if (loading)
    return (
      <Box h="100%" pos={'relative'}>
        <LoadingOverlay visible overlayBlur={1} />
      </Box>
    );

  return (
    <>
      <Page
        title={data?.getTaskById?.title}
        primaryAction={{
          content: 'Edit task',
          onClick: () => setOpened(true),
          loading: loading,
        }}
        secondaryActions={[
          {
            content: 'Delete',
            destructive: true,
            onClick: () => onDelete(),
            loading: loading || deleting,
          },
        ]}
      >
        <Text>{data.getTaskById.description}</Text>

        <Box>
          {data.getTaskById.tags.map((tag: any) => (
            <Badge key={tag.id} color="blue">
              {tag.name}
            </Badge>
          ))}
        </Box>
      </Page>

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        title="Edit Task"
        padding="md"
        size="md"
        position="right"
      >
        <TaskForm
          mode={FORM_MODE.EDIT}
          entityId={taskId}
          onSubmit={onSuccess}
        />
      </Drawer>
    </>
  );
};

export default TaskDetailPage;
